/**
 * DviWorkflowModal - Main container for the 7-stage DVI workflow
 * 
 * Responsive: Dialog on desktop, Sheet on mobile
 */

import { useState, useMemo } from 'react';
import { useIsDesktop } from '@/hooks/use-mobile';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { useOptiDvi } from '@/hooks/useOptiDvi';
import { useDviSession } from '@/hooks/useDviSession';
import { useDviFindingGroups } from '@/hooks/useDviFindingGroups';
import { useDviEstimate } from '@/hooks/useDviEstimate';
import { useDviMedia } from '@/hooks/useDviMedia';
import { DviWorkflowStepper, type WorkflowStage } from './DviWorkflowStepper';
import { DviStageSession } from './workflow/DviStageSession';
import { DviStageCapture } from './workflow/DviStageCapture';
import { DviStageGroup } from './workflow/DviStageGroup';
import { DviStageAnalyze } from './workflow/DviStageAnalyze';
import { DviStageEvidence } from './workflow/DviStageEvidence';
import { DviStageEstimate } from './workflow/DviStageEstimate';
import { DviStagePublish } from './workflow/DviStagePublish';

interface DviWorkflowModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ticketId: string;
  shopId: string;
  vehicle?: { year?: number; make?: string; model?: string };
  onJobsAdded?: () => void;
}

export function DviWorkflowModal({
  open,
  onOpenChange,
  ticketId,
  shopId,
  vehicle,
  onJobsAdded,
}: DviWorkflowModalProps) {
  const isDesktop = useIsDesktop();
  const { user } = useAuth();
  const dvi = useOptiDvi();
  const dviSession = useDviSession(ticketId, shopId);
  const sessionId = dviSession.session?.id || null;
  const findingGroups = useDviFindingGroups(sessionId, ticketId);
  const estimate = useDviEstimate(sessionId, ticketId);
  const media = useDviMedia(ticketId, sessionId);

  const [currentStage, setCurrentStage] = useState<WorkflowStage>(0);

  // Compute completed stages
  const completedStages = useMemo(() => {
    const completed: WorkflowStage[] = [];
    if (dviSession.session) completed.push(0);
    if (media.totalCount > 0) completed.push(1);
    if (findingGroups.findingGroups.length > 0) completed.push(2);
    if (findingGroups.analyzedGroups.length > 0) completed.push(3, 4);
    if (estimate.currentDraft) completed.push(5);
    if (dviSession.session?.status !== 'draft') completed.push(6);
    return completed;
  }, [dviSession.session, media.totalCount, findingGroups.findingGroups.length, findingGroups.analyzedGroups.length, estimate.currentDraft]);

  const renderStage = () => {
    switch (currentStage) {
      case 0:
        return (
          <DviStageSession
            session={dviSession.session}
            isLoading={dviSession.isLoadingSession}
            onCreateSession={dviSession.createSession}
            isCreating={dviSession.isCreatingSession}
            canCreate={dvi.canApprove}
          />
        );
      case 1:
        return (
          <DviStageCapture
            ticketId={ticketId}
            shopId={shopId}
            sessionId={sessionId}
            userId={user?.id || null}
            media={media.media}
            ungroupedCount={media.ungroupedCount}
            groupedCount={media.groupedCount}
            excludedCount={media.excludedCount}
            onToggleExclude={media.toggleExcluded}
            onDelete={media.deleteMedia}
            onCreateSession={dviSession.createSession}
            canUpload={dvi.canUpload}
            canDelete={dvi.canDelete}
            isLoading={media.isLoading}
            isDeleting={media.isDeleting}
            isToggling={media.isToggling}
          />
        );
      case 2:
        return (
          <DviStageGroup
            findingGroups={findingGroups.findingGroups}
            ungroupedCount={media.ungroupedCount}
            onAutoGroup={findingGroups.autoGroup}
            isAutoGrouping={findingGroups.isAutoGrouping}
            canApprove={dvi.canApprove}
            isLoading={findingGroups.isLoading}
          />
        );
      case 3:
        return (
          <DviStageAnalyze
            findingGroups={findingGroups.findingGroups}
            pendingGroups={findingGroups.pendingGroups}
            analyzedGroups={findingGroups.analyzedGroups}
            onAnalyzeGroup={findingGroups.analyzeGroup}
            onAnalyzeAll={() => findingGroups.pendingGroups.forEach((g) => findingGroups.analyzeGroup(g.id))}
            isAnalyzing={findingGroups.isAnalyzing}
            canAnalyze={dvi.canAnalyze}
            isLoading={findingGroups.isLoading}
          />
        );
      case 4:
        return (
          <DviStageEvidence
            analyzedGroups={findingGroups.analyzedGroups}
            canBuildEvidence={dvi.canApprove}
            isLoading={findingGroups.isLoading}
          />
        );
      case 5:
        return (
          <DviStageEstimate
            currentDraft={estimate.currentDraft}
            draftItems={estimate.draftItems}
            totalLaborMinutes={estimate.totalLaborMinutes}
            pendingReviewItems={estimate.pendingReviewItems}
            approvedItems={estimate.approvedItems}
            rejectedItems={estimate.rejectedItems}
            onBuildDraft={estimate.buildDraft}
            onApproveItems={estimate.approveItems}
            onRejectItems={estimate.rejectItems}
            onApplyItems={estimate.applyItems}
            onApplyAllApproved={estimate.applyAllApproved}
            isBuildingDraft={estimate.isBuildingDraft}
            isReviewing={estimate.isReviewing}
            isApplying={estimate.isApplying}
            canAddToTicket={dvi.canAddToTicket}
            canApprove={dvi.canApprove}
            hasAnalyzedGroups={findingGroups.analyzedGroups.length > 0}
            isLoading={estimate.isLoading}
            onJobsAdded={onJobsAdded}
          />
        );
      case 6:
        return (
          <DviStagePublish
            session={dviSession.session}
            findingGroups={findingGroups.findingGroups}
            approvedByCustomer={findingGroups.approvedByCustomer}
            declinedByCustomer={findingGroups.declinedByCustomer}
            onShare={dviSession.shareSession}
            isSharing={dviSession.isSharing}
            canApprove={dvi.canApprove}
            isLoading={dviSession.isLoadingSession}
          />
        );
      default:
        return null;
    }
  };

  const content = (
    <div className="flex flex-col h-full">
      <DviWorkflowStepper
        currentStage={currentStage}
        completedStages={completedStages}
        onStageClick={setCurrentStage}
        className="px-2 py-2 border-b shrink-0"
      />
      <div className="flex-1 overflow-y-auto p-4">
        {renderStage()}
      </div>
    </div>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col p-0">
          <DialogHeader className="px-4 py-3 border-b shrink-0">
            <DialogTitle>OptiDVI Workflow</DialogTitle>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[95vh] flex flex-col p-0">
        <SheetHeader className="px-4 py-3 border-b shrink-0">
          <SheetTitle>OptiDVI Workflow</SheetTitle>
        </SheetHeader>
        {content}
      </SheetContent>
    </Sheet>
  );
}
